import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Modal, Form, Spin } from 'antd';
import { FaRegTrashAlt, FaEdit } from 'react-icons/fa';
import { PlusOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const Unidade = () => {
  const [unidades, setUnidades] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [selectedUnidade, setSelectedUnidade] = useState(null);
  const [unidadeToDelete, setUnidadeToDelete] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const total = filteredData.length;
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, total);

  useEffect(() => {
    document.title = "Unidades";
    fetchUnidades();
  }, []);

  const sms_error = (msg) => toast.error(msg);
  const sms_success = (msg) => toast.success(msg);

  const fetchUnidades = async () => {
    setLoading(true);
    try {
      const response = await Api.ListUnidade();
      const dados = Array.isArray(response) ? response : [];
      
      const formatado = dados.map(item => ({
        key: item.id,
        descricao: item.descricao,
      }));
      
      setUnidades(formatado);
      setFilteredData(formatado);
    } catch (error) {
      console.error('Erro ao buscar unidades:', error);
      sms_error('Erro ao buscar unidades: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);

    const search = (value || '').toString().toLowerCase();
    const filtered = unidades.filter(item => {
      if (!item || typeof item.descricao !== 'string') return false;
      return item.descricao.toLowerCase().includes(search);
    });

    setFilteredData(filtered);
  };

  const handleAdd = async (values) => {
    setAddLoading(true);
    try {
      const result = await Api.AddUnidade({ descricao: values.descricao });
      if (result.success) {
        sms_success('Unidade adicionada com sucesso!');
        setIsAddModalVisible(false);
        form.resetFields();
        await fetchUnidades();
      } else {
        sms_error(result.message || 'Erro ao adicionar unidade');
      }
    } catch (error) {
      sms_error('Erro ao adicionar unidade: ' + error.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
      const result = await Api.AlterarUnidade({
        id: selectedUnidade.key,
        descricao: values.descricao,
      });
      if (result.success) {
        sms_success('Unidade editada com sucesso!');
        setIsEditModalVisible(false);
        editForm.resetFields();
        await fetchUnidades();
      } else {
        sms_error(result.message || 'Erro ao editar unidade');
      }
    } catch (error) {
      sms_error('Erro ao editar unidade: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const result = await Api.DeleteUnidade({ id: unidadeToDelete });
      if (result.success) {
        sms_success('Unidade excluída com sucesso!');
        await fetchUnidades();
      } else {
        sms_error(result.message || 'Erro ao excluir unidade');
      }
    } catch (error) {
      sms_error('Erro ao excluir unidade: ' + error.message);
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  const columns = [
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Pesquisar descrição"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              handleSearch(selectedKeys[0]);
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => {
              confirm();
              handleSearch(selectedKeys[0]);
            }}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Filtrar
          </Button>
          <Button onClick={() => {
            clearFilters();
            handleSearch('');
          }} size="small" style={{ width: 90 }}>
            Limpar
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        record.descricao.toLowerCase().includes(value.toLowerCase())
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 10 }}>
          <FaEdit
            style={{ cursor: 'pointer', color: '#1890ff' }}
            onClick={() => {
              setSelectedUnidade(record);
              editForm.setFieldsValue(record);
              setIsEditModalVisible(true);
            }}
          />
          <FaRegTrashAlt
            style={{ cursor: 'pointer', color: '#ff4d4f' }}
            onClick={() => {
              setUnidadeToDelete(record.key);
              setIsDeleteModalVisible(true);
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div>
      <ToastContainer />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => setIsAddModalVisible(true)}
        >
          Adicionar
        </Button>
      </div>

      <Spin spinning={loading} tip="Carregando...">
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredData.length,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          scroll={{ x: 'max-content' }}
          style={{ minWidth: '100%' }}
        />
      </Spin>

      <div style={{ marginBottom: 10 }}>
        <strong>
          {total > 0
            ? `Mostrando ${startIndex}–${endIndex} de ${total} unidades`
            : 'Nenhuma unidade encontrada'}
        </strong>
      </div>

      {/* Modal Adicionar */}
      <Modal
        title="Adicionar Unidade"
        visible={isAddModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsAddModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            name="descricao"
            label="Descrição"
            rules={[{ required: true, message: 'Descrição é obrigatória' }]}
          >
            <Input maxLength={10} />
          </Form.Item>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  form.resetFields();
                  setIsAddModalVisible(false);
                }}
                disabled={addLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={addLoading}>
                {addLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar */}
      <Modal
        title="Editar Unidade"
        visible={isEditModalVisible}
        onCancel={() => {
          editForm.resetFields();
          setIsEditModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            name="descricao"
            label="Descrição"
            rules={[{ required: true, message: 'Descrição é obrigatória' }]}
          >
            <Input maxLength={10} />
          </Form.Item>
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => {
                  editForm.resetFields();
                  setIsEditModalVisible(false);
                }}
                disabled={editLoading}
              >
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                {editLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Exclusão */}
      <Modal
        title="Confirmar Exclusão"
        visible={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Sim"
        cancelText="Não"
        confirmLoading={deleteLoading}
      >
        <p>Tem certeza que deseja excluir esta unidade?</p>
      </Modal>
    </div>
  );
};

export default Unidade;